import { getFormProps, getInputProps, getSelectProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { data, Form, redirect } from "react-router";
import z from "zod";
import { Field, SelectField } from "#app/components/forms2.tsx";
import { StatusButton } from "#app/components/ui/status-button.tsx";
import { requireUserId } from "#app/utils/auth.server.ts";
import { prisma } from "#app/utils/db.server.ts";
import { useIsPending } from "#app/utils/misc.tsx";
import { type Route } from "./+types/new";

const ChargeCategoryType = {
    Tax: "TAX",
    Fee: "FEE"
} as const

const chargeCategoryTypeOptions = [
    {
        value: ChargeCategoryType.Tax,
        name: "Tax"
    },
    {
        value: ChargeCategoryType.Fee,
        name: "Fee"
    },
]

export const ChargeCategoryEditorSchema = z.object({
    type: z.enum([ChargeCategoryType.Tax, ChargeCategoryType.Fee]),
    name: z.string(),
})

export async function action({ request }: Route.ActionArgs) {
    const userId = await requireUserId(request)
    const formData = await request.formData()
    const submission = parseWithZod(formData, {
        schema: ChargeCategoryEditorSchema,
    })

    if (submission.status !== 'success') {
        return data(
            { result: submission.reply() },
            { status: submission.status === 'error' ? 400 : 200 },
        )
    }

    const {
        type,
        name
	} = submission.value

    await prisma.$transaction(async (tx) => {
        const position = await tx.chargeCategory.count({ where: { userId, type } });
        return await tx.chargeCategory.create({
            data: {
                userId,
                type,
                name,
                position
            },
        })
    })

    return redirect("/chargeCategories")
}

export default function CreateChargeCategoryRoute({ actionData }: Route.ComponentProps) {
    const isPending = useIsPending()
    const [form, fields] = useForm({
        constraint: getZodConstraint(ChargeCategoryEditorSchema),
        lastResult: actionData?.result,
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: ChargeCategoryEditorSchema })
        },
        shouldRevalidate: 'onBlur',
    })
    return (
        <Form
            method="POST"
            {...getFormProps(form)}
            className="flex flex-col px-2 pt-12 pb-28 gap-2"
        >
            <div className="text-center text-xl font-medium">Create Charge Category</div>
            <SelectField
                labelProps={{ children: 'Type' }}
                selectProps={getSelectProps(fields.type)}
                items={chargeCategoryTypeOptions}
                errors={fields.type.errors}
            />
            <Field
                labelProps={{ children: 'Name' }}
                inputProps={getInputProps(fields.name, { type: 'text' })}
                errors={fields.name.errors}
            />
            <StatusButton
                type="submit"
                disabled={isPending}
                status={isPending ? 'pending' : 'idle'}
                className="cursor-pointer"
                name="intent"
                value="update"
            >
                Submit
            </StatusButton>
        </Form>
    )
}